import { useState, useMemo, useEffect } from 'react';
import { INSPIRATION_PROJECTS, STYLES, PRODUCTS, translateInspTitle, translateInspDesc } from '../store/productData';
import { useLanguage } from '../store/LanguageContext';
import SEOHead from '../components/SEOHead';
import { MapPin, Building } from 'lucide-react';

/** V5.33 (wens Franky): carrousel per huis. Kijkt naar /inspiration/iN.jpg
 *  plus varianten iN-2.jpg … iN-4.jpg — alles wat bestaat rolt automatisch
 *  mee; extra foto's toevoegen = gewoon het bestand plaatsen. Zolang er
 *  géén huisfoto is, valt hij terug op de steenfoto. De gekozen stenen
 *  staan als naamplaatjes op de foto zelf. */
function ProjectCarousel({ project, stoneNames, styleLabel, title }: {
  project: (typeof INSPIRATION_PROJECTS)[number];
  stoneNames: string[];
  styleLabel: string;
  title: string;
}) {
  const [slides, setSlides] = useState<string[]>([]);
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const candidates = [
      `/inspiration/${project.id}.jpg`,
      `/inspiration/${project.id}-2.jpg`,
      `/inspiration/${project.id}-3.jpg`,
      `/inspiration/${project.id}-4.jpg`,
    ];
    Promise.all(candidates.map(src => new Promise<string | null>(res => {
      const im = new Image();
      im.onload = () => res(src);
      im.onerror = () => res(null);
      im.src = src;
    }))).then(results => {
      if (cancelled) return;
      const ok = results.filter((x): x is string => !!x);
      setSlides(ok.length > 0 ? ok : [project.fallbackImage ?? project.image]);
    });
    return () => { cancelled = true; };
  }, [project.id, project.fallbackImage, project.image]);

  // Zachtjes doordraaien wanneer er meerdere foto's zijn.
  useEffect(() => {
    if (slides.length < 2) return;
    const timer = setInterval(() => setIdx(i => (i + 1) % slides.length), 4500);
    return () => clearInterval(timer);
  }, [slides.length]);

  return (
    <div className="relative h-72 md:h-80 overflow-hidden">
      {slides.map((src, i) => (
        <img
          key={src}
          src={src}
          alt={project.title}
          loading="lazy"
          className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ${i === idx ? 'opacity-100' : 'opacity-0'}`}
        />
      ))}
      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent pointer-events-none" />
      <div className="absolute top-4 left-4">
        <span className="px-3 py-1 text-[10px] font-bold tracking-wider uppercase bg-[#c47a3a] text-white rounded-sm capitalize">
          {styleLabel}
        </span>
      </div>
      {/* Steennaam-plaatjes op de foto */}
      <div className="absolute top-4 right-4 flex flex-col items-end gap-1.5">
        {stoneNames.map(n => (
          <span key={n} className="px-2.5 py-1 bg-black/60 backdrop-blur-sm text-white text-[10px] font-semibold rounded-sm">
            {n}
          </span>
        ))}
      </div>
      <div className="absolute bottom-0 left-0 right-0 p-6 pointer-events-none">
        <h3 className="text-2xl font-bold text-white">{title}</h3>
        <p className="text-sm text-gray-300 mt-1 flex items-center gap-1"><MapPin size={12} /> {project.location}</p>
      </div>
      {slides.length > 1 && (
        <div className="absolute bottom-2 right-3 flex gap-1.5">
          {slides.map((_, i) => (
            <button key={i} onClick={() => setIdx(i)}
              className={`w-2 h-2 rounded-full ${i === idx ? 'bg-white' : 'bg-white/40'}`} aria-label={`Foto ${i + 1}`} />
          ))}
        </div>
      )}
    </div>
  );
}

export default function InspirationPage() {
  const [activeStyle, setActiveStyle] = useState<string>('');
  const { t } = useLanguage();

  const filtered = useMemo(() => {
    if (!activeStyle) return INSPIRATION_PROJECTS;
    return INSPIRATION_PROJECTS.filter(p => p.style === activeStyle);
  }, [activeStyle]);

  const styleLabelMap: Record<string, string> = {
    modern: t('insp.style.modern'),
    landelijk: t('insp.style.landelijk'),
    industrieel: t('insp.style.industrieel'),
    klassiek: t('insp.style.klassiek'),
  };

  return (
    <div className="pt-20 md:pt-24">
      <SEOHead page="inspiration" />
      {/* V5.32: eigen fotobanner, zelfde hoogte als de andere pagina's */}
      <section className="relative h-[160px] md:h-[190px] flex items-center overflow-hidden text-white">
        <div className="absolute inset-0" style={{ backgroundImage: 'url(/products/muur-voorbeeld-4.jpg)', backgroundSize: 'cover', backgroundPosition: 'center' }} />
        <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/55 to-black/30" />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <span className="text-[#c47a3a] text-sm font-semibold tracking-wider uppercase">{t('insp.realisations')}</span>
          <h1 className="text-2xl md:text-3xl font-bold mt-1 mb-1.5" style={{ fontFamily: 'Playfair Display' }}>{t('insp.title')}</h1>
          <p className="text-gray-300 max-w-xl">{t('insp.desc')}</p>
        </div>
      </section>

      <section className="bg-white sticky top-16 md:top-20 z-30 border-b border-gray-100 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex flex-wrap gap-2">
          <button onClick={() => setActiveStyle('')}
            className={`px-5 py-2 rounded-sm text-sm font-medium transition-all ${!activeStyle ? 'bg-[#c47a3a] text-white' : 'bg-[#faf7f3] text-[#4a4a4a] hover:bg-gray-200'}`}>
            {t('insp.allStyles')}
          </button>
          {STYLES.map(s => (
            <button key={s} onClick={() => setActiveStyle(s)}
              className={`px-5 py-2 rounded-sm text-sm font-medium transition-all capitalize ${activeStyle === s ? 'bg-[#c47a3a] text-white' : 'bg-[#faf7f3] text-[#4a4a4a] hover:bg-gray-200'}`}>
              {styleLabelMap[s]}
            </button>
          ))}
        </div>
      </section>

      <section className="section-padding bg-[#faf7f3]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {filtered.map(project => (
              <article key={project.id} className="bg-white rounded-lg overflow-hidden shadow-sm hover:shadow-xl transition-all duration-500 group">
                <ProjectCarousel
                  project={project}
                  title={translateInspTitle(project.id, t)}
                  styleLabel={styleLabelMap[project.style]}
                  stoneNames={project.products
                    .map(pid => PRODUCTS.find(p => p.id === pid)?.name)
                    .filter((n): n is string => !!n)}
                />
                <div className="p-6">
                  <p className="text-[#8a7e74] text-sm leading-relaxed mb-4">{translateInspDesc(project.id, t)}</p>
                  {project.architect && <p className="text-xs text-[#8a7e74] mb-4">{t('insp.architect')}: {project.architect}</p>}
                  <div className="flex flex-wrap gap-2">
                    {project.products.map(pid => {
                      const prod = PRODUCTS.find(p => p.id === pid);
                      return prod ? (
                        <span key={pid} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#faf7f3] text-xs font-medium rounded-sm">
                          <span className="w-3 h-3 rounded-full" style={{ backgroundColor: prod.colorHex }} />
                          {prod.name}
                        </span>
                      ) : null;
                    })}
                    {/* v5 blok 3: gebruikt voegtype vermelden bij elk project */}
                    {project.grout && (
                      <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#faf7f3] text-xs font-medium rounded-sm text-[#8a7e74]">
                        Voeg: {project.grout}
                      </span>
                    )}
                  </div>
                </div>
              </article>
            ))}
          </div>
          {filtered.length === 0 && (
            <div className="text-center py-20">
              <div className="w-16 h-16 rounded-2xl bg-[#c47a3a]/10 flex items-center justify-center mb-4 mx-auto"><Building size={32} className="text-[#c47a3a]" /></div>
              <h3 className="text-xl font-bold mb-2">{t('insp.noResults')}</h3>
              <p className="text-[#8a7e74]">{t('insp.noResultsDesc')}</p>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
